import { Children, useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'

import { ListboxContext } from './ListboxContext'
import { ListboxItemProps } from './ListboxItem'

import styles from './Listbox.module.scss'

export type ListboxProps = {
  ref?: React.RefObject<HTMLUListElement | null>
  children?: React.ReactNode
  id: string
  ariaLabel?: string
  selectedOptionId: string | string[] | null
  onSelect: (id: string) => void
  ariaRequired?: boolean
  onBlur?: React.FocusEventHandler<HTMLUListElement>
  props?: React.HTMLAttributes<HTMLUListElement>
  className?: string
}

/**
 * Listbox — simple <ul> wrapper for ListboxItem components (list for single choice), that enabled keyboard navigation and accessibility features.
 * Use listbox for select-like (radio-like) components with complex content (eg. with selectable descendants).
 *
 * Attention!
 * Don't use this for menu-like components (toolbars or lists with action triggers), since menus have different keyboard navigation and accessibility requirements.
 * Don't use this for simple navigation menus (eg. site header navigation) - they don't need keyboard arrows navigation. Use native <nav> and <ul> elements for that instead.
 * Don't use this for radio input (single choice options labeled with only text) - use RadioButton component instead.
 */
const Listbox = ({
  children,
  id,
  ariaLabel,
  onSelect,
  selectedOptionId,
  ariaRequired = false,
  ref,
  className,
  ...props
}: ListboxProps) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const listboxItemsArray: React.ReactElement<ListboxItemProps>[] = useMemo(
    () => Children.toArray(children) as React.ReactElement<ListboxItemProps>[],
    [children]
  )

  const count = listboxItemsArray.length

  const focusedOptionId = useMemo(() => {
    if (focusedIndex < 0 || focusedIndex >= count) return null

    return listboxItemsArray[focusedIndex]?.props.optionId || null
  }, [focusedIndex, listboxItemsArray, count])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (count === 0) return

      const getItemId = (index: number): string | null => {
        if (index < 0 || index >= count) return null

        return listboxItemsArray[index]?.props.optionId || null
      }

      const next = () => {
        setFocusedIndex((focusedIndex + 1) % count)
      }
      const prev = () => {
        setFocusedIndex((focusedIndex - 1 + count) % count)
      }
      const first = () => {
        setFocusedIndex(0)
      }
      const last = () => {
        setFocusedIndex(count - 1)
      }

      const select = () => {
        const focusedId = getItemId(focusedIndex)

        if (focusedId) {
          onSelect(focusedId)
        }
      }

      const keyMap: Record<string, () => void> = {
        ArrowDown: next,
        ArrowUp: prev,
        ArrowRight: next,
        ArrowLeft: prev,
        Home: first,
        End: last,
        Space: select,
        Enter: select
      }

      const action = keyMap[event.key]

      if (event.key === 'Escape') {
        event.preventDefault()
        setFocusedIndex(0)
      }

      if (action) {
        event.preventDefault()
        action()
      }
    },
    [count, focusedIndex, listboxItemsArray, onSelect]
  )

  const selectedOptionIds = useMemo(() => {
    if (selectedOptionId === null || selectedOptionId === undefined) return []

    return Array.isArray(selectedOptionId)
      ? selectedOptionId
      : [selectedOptionId]
  }, [selectedOptionId])

  return (
    <ul
      ref={ref}
      id={id}
      role='listbox'
      aria-required={ariaRequired}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (focusedIndex === -1 && count > 0) {
          const initialIndex = selectedOptionId
            ? listboxItemsArray.findIndex(
                el => el.props.optionId === selectedOptionId
              )
            : 0

          setFocusedIndex(initialIndex)
        }
      }}
      tabIndex={focusedIndex === -1 ? 0 : -1}
      aria-activedescendant={
        focusedOptionId ? `${id}-${focusedOptionId}` : undefined
      }
      {...props}
      className={clsx(styles.listbox, className)}
    >
      <ListboxContext.Provider
        value={{
          id,
          selectedOptionIds,
          focusedOptionId,
          onSelect
        }}
      >
        {children}
      </ListboxContext.Provider>
    </ul>
  )
}

export default Listbox
